import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { useApp } from '../context/AppContext';
import { DirectiveTree } from '../components/cabinet/DirectiveTree';
import { AssignmentRow } from '../components/cabinet/AssignmentRow';
import { DecisionTimeline } from '../components/cabinet/DecisionTimeline';
import { EscalationTimeline } from '../components/compliance/EscalationTimeline';
import { clsx } from 'clsx';
import { format, differenceInDays, isPast } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency } from '../utils/helpers';

/**
 * Priority-to-gradient mapping for the decision header banner.
 */
const PRIORITY_GRADIENTS = {
  critical: 'from-red-600 to-red-800',
  high: 'from-orange-500 to-orange-700',
  medium: 'from-amber-500 to-amber-700',
  standard: 'from-slate-500 to-slate-700',
};

/**
 * Category display labels.
 */
const CATEGORY_LABELS = {
  POLICY: 'Policy Directive',
  STATUTORY: 'Statutory Submission',
  ADMIN: 'Administrative',
  LEGISLATION: 'Legislation',
  EMERGENCY: 'Emergency Response',
  BUDGET: 'Budget Allocation',
  RATIFICATION: 'Ratification',
};

/**
 * Tab configuration with icons and optional count derivation keys.
 */
const TABS = [
  { id: 'overview', label: 'Overview', icon: FiIcons.FiLayout },
  { id: 'directives', label: 'Directives', icon: FiIcons.FiGitBranch, countKey: 'directives' },
  { id: 'assignments', label: 'Assignments', icon: FiIcons.FiUsers, countKey: 'assignments' },
  { id: 'timeline', label: 'Timeline', icon: FiIcons.FiClock },
  { id: 'evidence', label: 'Evidence', icon: FiIcons.FiPaperclip, countKey: 'evidence' },
  { id: 'escalations', label: 'Escalations', icon: FiIcons.FiAlertTriangle, countKey: 'escalations' },
];

/**
 * Returns a Tailwind color class for a progress bar based on the percentage.
 * @param {number} progress - 0 to 100
 * @returns {string}
 */
const getProgressColor = (progress) => {
  if (progress >= 70) return 'bg-grn-green';
  if (progress >= 30) return 'bg-amber-500';
  return 'bg-red-500';
};

/**
 * Resolves a file type string to a Feather icon component.
 * @param {string} fileType
 * @returns {Function}
 */
const getFileIcon = (fileType) => {
  const t = (fileType || '').toLowerCase();
  if (t.includes('pdf')) return FiIcons.FiFileText;
  if (t.includes('zip') || t.includes('rar')) return FiIcons.FiArchive;
  if (t.includes('image') || t.includes('jpg') || t.includes('png')) return FiIcons.FiImage;
  if (t.includes('xls') || t.includes('csv')) return FiIcons.FiGrid;
  return FiIcons.FiFile;
};

/**
 * CabinetDecisionDetail -- full detail page for a single cabinet decision.
 *
 * Displays a breadcrumb, priority-conditional gradient header, and a
 * 6-tab content area: Overview, Directives, Assignments, Timeline,
 * Evidence, and Escalations.
 */
export const CabinetDecisionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data } = useApp();

  const [activeTab, setActiveTab] = useState('overview');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('all');
  const [assignmentMinistryFilter, setAssignmentMinistryFilter] = useState('all');

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const decision = useMemo(
    () => data.cabinetDecisions?.find((d) => d.id === id),
    [data.cabinetDecisions, id]
  );

  const directives = useMemo(
    () => (data.cabinetDirectives || []).filter((d) => d.parentDecisionId === id),
    [data.cabinetDirectives, id]
  );

  const assignments = useMemo(
    () =>
      (data.cabinetAssignments || []).filter((a) =>
        directives.some((d) => d.id === a.parentDirectiveId)
      ),
    [data.cabinetAssignments, directives]
  );

  /** Collect all evidence items across directives. */
  const allEvidence = useMemo(() => {
    return directives.reduce((acc, dir) => {
      if (dir.evidence && dir.evidence.length > 0) {
        acc.push({ directiveId: dir.id, directiveTitle: dir.title, items: dir.evidence });
      }
      return acc;
    }, []);
  }, [directives]);

  /** Filter escalation events from the decision audit trail. */
  const escalationEvents = useMemo(() => {
    if (!decision) return [];
    const trail = decision.auditTrail || [];
    return trail.filter(
      (e) =>
        (e.action || '').toLowerCase().includes('escalat') ||
        (e.type || '').toLowerCase().includes('escalat')
    );
  }, [decision]);

  /** Compute tab badge counts. */
  const tabCounts = useMemo(
    () => ({
      directives: directives.length,
      assignments: assignments.length,
      evidence: allEvidence.reduce((sum, g) => sum + g.items.length, 0),
      escalations: (decision?.escalationHistory || []).length + escalationEvents.length,
    }),
    [directives, assignments, allEvidence, decision, escalationEvents]
  );

  /** Filtered assignments for the Assignments tab. */
  const filteredAssignments = useMemo(() => {
    let list = assignments;
    if (assignmentStatusFilter !== 'all') {
      list = list.filter((a) => a.status === assignmentStatusFilter);
    }
    if (assignmentMinistryFilter !== 'all') {
      list = list.filter((a) => a.assignedTo?.ministry === assignmentMinistryFilter);
    }
    return list;
  }, [assignments, assignmentStatusFilter, assignmentMinistryFilter]);

  /** Unique ministries across all assignments, for the filter dropdown. */
  const assignmentMinistries = useMemo(() => {
    const set = new Set();
    assignments.forEach((a) => {
      if (a.assignedTo?.ministry) set.add(a.assignedTo.ministry);
    });
    return [...set].sort();
  }, [assignments]);

  /** Summary stats for the assignments tab header. */
  const assignmentStats = useMemo(() => {
    const total = assignments.length;
    const completed = assignments.filter((a) => a.status === 'Completed').length;
    const inProgress = assignments.filter((a) => a.status === 'In Progress').length;
    const overdue = assignments.filter((a) => a.status === 'Overdue').length;
    const pending = assignments.filter((a) => a.status === 'Pending').length;
    return { total, completed, inProgress, overdue, pending };
  }, [assignments]);

  // ---------------------------------------------------------------------------
  // Not-found state
  // ---------------------------------------------------------------------------
  if (!decision) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="text-5xl text-slate-300 mb-4" />
        <h2 className="text-xl font-bold text-slate-700 mb-2">Decision Not Found</h2>
        <p className="text-sm text-slate-500 mb-6">
          No cabinet decision matches the identifier <span className="font-mono">{id}</span>.
        </p>
        <Button variant="outline" icon={FiIcons.FiArrowLeft} onClick={() => navigate('/cabinet-decisions')}>
          Back to Cabinet Decisions
        </Button>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Derived header values
  // ---------------------------------------------------------------------------
  const priority = decision.priority || 'standard';
  const gradient = PRIORITY_GRADIENTS[priority] || PRIORITY_GRADIENTS.standard;
  const leadMinistry =
    decision.responsibleMinistries?.find((m) => m.role === 'Lead')?.ministryName || 'Not assigned';
  const deadlineDate = decision.deadline ? new Date(decision.deadline) : null;
  const daysUntilDeadline = deadlineDate ? differenceInDays(deadlineDate, new Date()) : null;
  const isOverdue = deadlineDate ? isPast(deadlineDate) && decision.status !== 'Completed' : false;
  const progress = decision.overallProgress ?? 0;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      {/* ===== Breadcrumb ===== */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <button
          onClick={() => navigate('/cabinet-decisions')}
          className="hover:text-foreground transition-colors flex items-center gap-1"
        >
          <SafeIcon icon={FiIcons.FiBookOpen} size={14} />
          Cabinet Decisions
        </button>
        <SafeIcon icon={FiIcons.FiChevronRight} size={14} />
        <span className="text-foreground font-medium truncate">{decision.referenceNumber}</span>
      </nav>

      {/* ===== Decision Header Banner ===== */}
      <div className={clsx('rounded-xl bg-gradient-to-r text-white p-6 shadow-lg', gradient)}>
        {/* Top row: back + action buttons */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/cabinet-decisions')}
            className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} size={16} />
            Back
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" icon={FiIcons.FiEdit2} className="text-white border-white/30 hover:bg-white/10">
              Edit Decision
            </Button>
            <Button variant="ghost" size="sm" icon={FiIcons.FiGitBranch} className="text-white border-white/30 hover:bg-white/10">
              Add Directive
            </Button>
            <Button variant="ghost" size="sm" icon={FiIcons.FiAlertTriangle} className="text-white border-white/30 hover:bg-white/10">
              Escalate
            </Button>
            <Button variant="ghost" size="sm" icon={FiIcons.FiDownload} className="text-white border-white/30 hover:bg-white/10">
              Export PDF
            </Button>
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="px-2.5 py-1 rounded-md text-xs font-bold uppercase bg-white/20 backdrop-blur-sm">
            {priority}
          </span>
          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-white/20 backdrop-blur-sm">
            {CATEGORY_LABELS[decision.category] || decision.category}
          </span>
          <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-white/20 backdrop-blur-sm">
            {decision.status}
          </span>
          {decision.classification && (
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-white/20 backdrop-blur-sm">
              {decision.classification}
            </span>
          )}
        </div>

        {/* Reference + Title */}
        <p className="font-mono text-grn-gold text-sm mb-1">
          {decision.referenceNumber}
          {decision.cdn ? ` | CDN: ${decision.cdn}` : ''}
        </p>
        <h1 className="text-2xl font-bold leading-tight mb-4">{decision.title}</h1>

        {/* Metadata row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider mb-0.5">Lead Ministry</p>
            <p className="text-sm font-medium">{leadMinistry}</p>
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider mb-0.5">Date Decided</p>
            <p className="text-sm font-medium">
              {decision.dateDecided ? format(new Date(decision.dateDecided), 'dd MMM yyyy') : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider mb-0.5">Deadline</p>
            <p className={clsx('text-sm font-medium', isOverdue && 'text-red-200 font-bold')}>
              {deadlineDate ? format(deadlineDate, 'dd MMM yyyy') : 'No deadline'}
              {isOverdue && ' (OVERDUE)'}
              {!isOverdue && daysUntilDeadline !== null && daysUntilDeadline <= 30 && (
                <span className="ml-1 text-xs text-white/70">({daysUntilDeadline}d left)</span>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-white/60 uppercase tracking-wider mb-0.5">Progress</p>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-sm font-bold">{progress}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Tab Bar ===== */}
      <div className="border-b border-border">
        <nav className="flex gap-1 -mb-px overflow-x-auto" role="tablist">
          {TABS.map((tab) => {
            const count = tab.countKey ? tabCounts[tab.countKey] : null;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  isActive
                    ? 'border-grn-navy text-grn-navy'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-slate-300'
                )}
              >
                <SafeIcon icon={tab.icon} size={15} />
                {tab.label}
                {count !== null && count > 0 && (
                  <span
                    className={clsx(
                      'ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                      isActive ? 'bg-grn-navy/10 text-grn-navy' : 'bg-slate-100 text-slate-500'
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ===== Tab Content ===== */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <OverviewTab
              decision={decision}
              directives={directives}
              assignments={assignments}
              leadMinistry={leadMinistry}
              progress={progress}
            />
          )}

          {activeTab === 'directives' && (
            <DirectivesTab
              decision={decision}
              directives={directives}
              assignments={assignments}
            />
          )}

          {activeTab === 'assignments' && (
            <AssignmentsTab
              assignments={filteredAssignments}
              directives={directives}
              stats={assignmentStats}
              statusFilter={assignmentStatusFilter}
              onStatusFilterChange={setAssignmentStatusFilter}
              ministryFilter={assignmentMinistryFilter}
              onMinistryFilterChange={setAssignmentMinistryFilter}
              ministries={assignmentMinistries}
            />
          )}

          {activeTab === 'timeline' && (
            <TimelineTab decision={decision} directives={directives} assignments={assignments} />
          )}

          {activeTab === 'evidence' && (
            <EvidenceTab decision={decision} allEvidence={allEvidence} />
          )}

          {activeTab === 'escalations' && (
            <EscalationsTab
              decision={decision}
              escalationEvents={escalationEvents}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </motion.div>
  );
};

// =============================================================================
// TAB 1: Overview
// =============================================================================

const OverviewTab = ({ decision, directives, assignments, leadMinistry, progress }) => {
  const supportingMinistries = (decision.responsibleMinistries || []).filter(
    (m) => m.role !== 'Lead'
  );
  const budget = decision.budgetImplication || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left column (2/3) -- Metadata card */}
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiFileText} className="text-grn-navy" />
            Decision Details
          </h3>

          {/* Description */}
          {decision.description && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Description
              </p>
              <p className="text-sm text-foreground leading-relaxed">{decision.description}</p>
            </div>
          )}

          {/* Full text */}
          {decision.fullText && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Full Resolution Text
              </p>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                {decision.fullText}
              </p>
            </div>
          )}

          {/* Legal Basis */}
          {decision.legalBasis && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Legal Basis
              </p>
              <p className="text-sm text-foreground">{decision.legalBasis}</p>
            </div>
          )}

          {/* Notes */}
          {decision.notes && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Notes
              </p>
              <p className="text-sm text-muted-foreground italic">{decision.notes}</p>
            </div>
          )}

          {/* Responsible Ministries */}
          <div className="mb-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
              Responsible Ministries
            </p>
            <div className="space-y-2">
              {(decision.responsibleMinistries || []).map((m, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-muted/50 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiBriefcase} size={14} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      {m.ministryName || m.ministryId}
                    </span>
                  </div>
                  <Badge
                    status={m.role === 'Lead' ? 'On Track' : m.role === 'Co-lead' ? 'In Progress' : 'Pending'}
                    size="sm"
                  >
                    {m.role}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* NDP6 Alignment */}
          {decision.ndp6Pillar && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                NDP6 Alignment
              </p>
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium text-foreground">
                  Pillar: {decision.ndp6Pillar}
                </p>
                {decision.ndp6ThematicArea && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Thematic Area: {decision.ndp6ThematicArea}
                  </p>
                )}
                {decision.ndp6FocusArea && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Focus Area: {decision.ndp6FocusArea}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Linked Projects */}
          {decision.linkedProjectIds && decision.linkedProjectIds.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Linked Projects
              </p>
              <div className="flex flex-wrap gap-2">
                {decision.linkedProjectIds.map((pid) => (
                  <span
                    key={pid}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200"
                  >
                    <SafeIcon icon={FiIcons.FiFolder} size={12} />
                    {pid}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Budget Implication */}
          {budget.hasBudgetImpact && (
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">
                Budget Information
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Estimated Cost</p>
                  <p className="text-sm font-bold text-foreground">
                    {budget.estimatedCost ? formatCurrency(budget.estimatedCost) : 'N/A'}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Funding Source</p>
                  <p className="text-sm font-medium text-foreground">
                    {budget.fundingSource || 'N/A'}
                  </p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">Financial Year</p>
                  <p className="text-sm font-medium text-foreground">
                    {budget.financialYear || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Right column (1/3) -- Progress summary */}
      <div className="space-y-6">
        <Card>
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiTrendingUp} className="text-grn-navy" />
            Progress Summary
          </h3>

          {/* Circular-style overall percentage */}
          <div className="flex flex-col items-center mb-6">
            <div className="relative w-28 h-28">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  className="text-slate-100"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeDasharray={`${progress * 2.64} 264`}
                  strokeLinecap="round"
                  className={clsx(
                    progress >= 70 ? 'text-grn-green' : progress >= 30 ? 'text-amber-500' : 'text-red-500'
                  )}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-foreground">{progress}%</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Overall Implementation</p>
          </div>

          {/* Per-directive progress bars */}
          <div className="space-y-3 mb-6">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Directive Progress
            </p>
            {directives.length > 0 ? (
              directives.map((dir) => {
                const dp = dir.progress || 0;
                return (
                  <div key={dir.id}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-foreground truncate max-w-[80%]">
                        {dir.title || dir.referenceCode || dir.id}
                      </p>
                      <span className="text-xs font-bold text-foreground">{dp}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={clsx('h-full rounded-full transition-all', getProgressColor(dp))}
                        style={{ width: `${dp}%` }}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-muted-foreground italic">No directives yet.</p>
            )}
          </div>

          {/* Key Dates */}
          <div className="space-y-2 mb-6">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Key Dates
            </p>
            <DateRow
              label="Submission"
              date={decision.submissionDate}
              icon={FiIcons.FiSend}
            />
            <DateRow
              label="Decided"
              date={decision.dateDecided}
              icon={FiIcons.FiCheckSquare}
            />
            <DateRow
              label="Communicated"
              date={decision.communicatedDate}
              icon={FiIcons.FiMail}
            />
            <DateRow
              label="Deadline"
              date={decision.deadline}
              icon={FiIcons.FiFlag}
              highlight={
                decision.deadline && isPast(new Date(decision.deadline)) && decision.status !== 'Completed'
              }
            />
            {decision.actualCompletionDate && (
              <DateRow
                label="Completed"
                date={decision.actualCompletionDate}
                icon={FiIcons.FiAward}
              />
            )}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 gap-3">
            <MiniStat
              label="Directives"
              value={directives.length}
              icon={FiIcons.FiGitBranch}
            />
            <MiniStat
              label="Assignments"
              value={assignments.length}
              icon={FiIcons.FiUsers}
            />
            <MiniStat
              label="Completed"
              value={directives.filter((d) => d.status === 'Completed').length}
              icon={FiIcons.FiCheckCircle}
              color="text-grn-green"
            />
            <MiniStat
              label="Overdue"
              value={directives.filter((d) => d.status === 'Overdue').length}
              icon={FiIcons.FiAlertTriangle}
              color="text-red-500"
            />
          </div>
        </Card>

        {/* Quick Actions Card */}
        <Card>
          <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiZap} className="text-grn-gold" />
            Quick Actions
          </h3>
          <div className="space-y-2">
            <Button variant="outline" size="sm" icon={FiIcons.FiGitBranch} className="w-full justify-start">
              Add New Directive
            </Button>
            <Button variant="outline" size="sm" icon={FiIcons.FiUpload} className="w-full justify-start">
              Upload Evidence
            </Button>
            <Button variant="outline" size="sm" icon={FiIcons.FiFileText} className="w-full justify-start">
              Generate Report
            </Button>
            <Button variant="outline" size="sm" icon={FiIcons.FiBell} className="w-full justify-start">
              Send Reminder
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

// =============================================================================
// TAB 2: Directives
// =============================================================================

const DirectivesTab = ({ decision, directives, assignments }) => {
  return (
    <Card>
      <DirectiveTree
        decision={decision}
        directives={directives}
        assignments={assignments}
      />
    </Card>
  );
};

// =============================================================================
// TAB 3: Assignments
// =============================================================================

const AssignmentsTab = ({
  assignments,
  directives,
  stats,
  statusFilter,
  onStatusFilterChange,
  ministryFilter,
  onMinistryFilterChange,
  ministries,
}) => {
  return (
    <div className="space-y-4">
      {/* Summary stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBadge label="Total" value={stats.total} icon={FiIcons.FiUsers} color="text-slate-600 bg-slate-50" />
        <StatBadge label="Completed" value={stats.completed} icon={FiIcons.FiCheckCircle} color="text-green-600 bg-green-50" />
        <StatBadge label="In Progress" value={stats.inProgress} icon={FiIcons.FiLoader} color="text-blue-600 bg-blue-50" />
        <StatBadge label="Overdue" value={stats.overdue} icon={FiIcons.FiAlertTriangle} color="text-red-600 bg-red-50" />
        <StatBadge label="Pending" value={stats.pending} icon={FiIcons.FiClock} color="text-amber-600 bg-amber-50" />
      </div>

      {/* Mini-filter bar */}
      <Card className="!p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiFilter} size={14} className="text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Filter:</span>
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            className="h-8 text-xs w-auto min-w-[140px]"
          >
            <option value="all">All Statuses</option>
            <option value="Completed">Completed</option>
            <option value="In Progress">In Progress</option>
            <option value="Overdue">Overdue</option>
            <option value="Pending">Pending</option>
            <option value="Blocked">Blocked</option>
          </Select>
          <Select
            value={ministryFilter}
            onChange={(e) => onMinistryFilterChange(e.target.value)}
            className="h-8 text-xs w-auto min-w-[160px]"
          >
            <option value="all">All Ministries</option>
            {ministries.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
          <span className="text-xs text-muted-foreground ml-auto">
            Showing {assignments.length} of {stats.total} assignments
          </span>
        </div>
      </Card>

      {/* Table header */}
      <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-slate-50 rounded-t-lg border border-b-0 border-border text-xs font-bold text-muted-foreground uppercase tracking-wider">
        <span className="w-8" />
        <span className="flex-1">Assignee</span>
        <span className="flex-1">Task</span>
        <span className="min-w-[140px]">Deadline</span>
        <span className="min-w-[80px] hidden lg:block">Progress</span>
        <span className="min-w-[100px]">Status</span>
        <span className="min-w-[100px]">Actions</span>
      </div>

      {/* Assignment rows */}
      <div className="border border-border rounded-b-lg md:rounded-lg overflow-hidden">
        {assignments.length > 0 ? (
          assignments.map((assignment) => (
            <AssignmentRow
              key={assignment.id}
              assignment={assignment}
              variant="table"
            />
          ))
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiIcons.FiInbox} className="mx-auto text-3xl text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground">
              No assignments match the current filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// TAB 4: Timeline
// =============================================================================

const TimelineTab = ({ decision, directives, assignments }) => {
  /** Build directive events from their status data. */
  const directiveEvents = useMemo(() => {
    return directives.map((dir) => ({
      id: `dir-evt-${dir.id}`,
      type: dir.status === 'Completed' ? 'directive_completed' :
            dir.status === 'Overdue' ? 'overdue' :
            dir.status === 'In Progress' ? 'in_progress' :
            'directive_created',
      date: dir.lastUpdated || dir.createdDate || dir.startDate,
      action: `Directive "${dir.title}" -- ${dir.status}`,
      actor: dir.assigneeName || dir.assignedEntityName,
      directiveRef: dir.referenceCode || dir.id,
      status: dir.status,
    }));
  }, [directives]);

  /** Build assignment events from completed assignments. */
  const assignmentEvents = useMemo(() => {
    return assignments
      .filter((a) => a.status === 'Completed' && a.completedDate)
      .map((a) => ({
        id: `asg-evt-${a.id}`,
        type: 'assignment_completed',
        date: a.completedDate,
        action: `Assignment completed: ${a.taskDescription}`,
        actor: a.assignedTo?.name,
        assignmentRef: a.id,
        status: 'Completed',
      }));
  }, [assignments]);

  return (
    <Card>
      <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
        <SafeIcon icon={FiIcons.FiClock} className="text-grn-navy" />
        Decision Lifecycle Timeline
      </h3>
      <DecisionTimeline
        auditTrail={decision.auditTrail || []}
        directiveEvents={directiveEvents}
        assignmentEvents={assignmentEvents}
      />
    </Card>
  );
};

// =============================================================================
// TAB 5: Evidence
// =============================================================================

const EvidenceTab = ({ decision, allEvidence }) => {
  const decisionDocs = decision.attachments || [];

  return (
    <div className="space-y-6">
      {/* Decision-level documents */}
      {decisionDocs.length > 0 && (
        <Card>
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiArchive} className="text-grn-navy" />
            Decision Documents
          </h3>
          <div className="space-y-2">
            {decisionDocs.map((doc) => (
              <EvidenceItem
                key={doc.id}
                name={doc.name}
                type={doc.fileType || doc.type}
                uploadedBy={doc.uploadedBy}
                uploadedDate={doc.uploadedDate}
                classification={doc.classification}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Evidence grouped by directive */}
      {allEvidence.length > 0 ? (
        allEvidence.map((group) => (
          <Card key={group.directiveId}>
            <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiGitBranch} size={14} className="text-grn-navy" />
              {group.directiveTitle || group.directiveId}
            </h4>
            <div className="space-y-2">
              {group.items.map((item) => (
                <EvidenceItem
                  key={item.id}
                  name={item.title || item.name}
                  type={item.fileType || item.type}
                  uploadedBy={item.uploadedBy}
                  uploadedDate={item.uploadedDate}
                  verifiedBy={item.verifiedBy}
                  verifiedDate={item.verifiedDate}
                />
              ))}
            </div>
          </Card>
        ))
      ) : decisionDocs.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <SafeIcon icon={FiIcons.FiInbox} className="mx-auto text-3xl text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground mb-1">No evidence uploaded yet.</p>
            <p className="text-xs text-slate-400">
              Evidence documents will appear here once uploaded against directives.
            </p>
          </div>
        </Card>
      ) : null}

      {/* Upload button placeholder */}
      <div className="flex justify-end">
        <Button variant="default" icon={FiIcons.FiUpload}>
          Upload Evidence
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// TAB 6: Escalations
// =============================================================================

const EscalationsTab = ({ decision, escalationEvents }) => {
  const escalationHistory = decision.escalationHistory || [];
  const hasEscalations = escalationHistory.length > 0 || escalationEvents.length > 0;

  /** Map audit-trail escalation events into the shape EscalationTimeline expects. */
  const mappedEscalations = useMemo(() => {
    return escalationHistory.map((e, idx) => ({
      ...e,
      timestamp: e.timestamp || e.date,
      type: e.type || 'formal_notice',
      urgency: e.urgency || 'standard',
      status: e.status || 'pending',
      recipient: e.recipient || { officer: 'N/A', ministry: 'N/A' },
      initiatedBy: e.initiatedBy || e.actor || "PM's Office",
      notes: e.notes || e.action || '',
    }));
  }, [escalationHistory]);

  return (
    <div className="space-y-6">
      {/* Escalation summary */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-amber-500" />
            Escalation History
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Escalation Level: <span className="font-bold text-foreground">{decision.escalationLevel ?? 0}/5</span>
            </span>
            <Button variant="destructive" size="sm" icon={FiIcons.FiAlertTriangle}>
              New Escalation
            </Button>
          </div>
        </div>

        {/* Escalation level indicator */}
        <div className="mb-6">
          <div className="flex items-center gap-1 mb-2">
            {[0, 1, 2, 3, 4, 5].map((level) => (
              <div
                key={level}
                className={clsx(
                  'flex-1 h-2 rounded-full transition-all',
                  level <= (decision.escalationLevel ?? 0)
                    ? level <= 1
                      ? 'bg-green-400'
                      : level <= 3
                      ? 'bg-amber-400'
                      : 'bg-red-500'
                    : 'bg-slate-100'
                )}
              />
            ))}
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground uppercase tracking-wider">
            <span>Auto Reminder</span>
            <span>Ministry</span>
            <span>PM Office</span>
            <span>Cabinet</span>
            <span>President</span>
            <span>Public</span>
          </div>
        </div>

        {hasEscalations ? (
          <EscalationTimeline escalations={mappedEscalations} />
        ) : (
          <div className="text-center py-8">
            <SafeIcon icon={FiIcons.FiShield} className="mx-auto text-3xl text-slate-300 mb-2" />
            <p className="text-sm text-muted-foreground mb-1">No escalations recorded.</p>
            <p className="text-xs text-slate-400">
              Escalation events will appear here when deadlines are breached or manual escalations are initiated.
            </p>
          </div>
        )}
      </Card>

      {/* Audit trail escalation events (if any separate from history) */}
      {escalationEvents.length > 0 && (
        <Card>
          <h4 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiClock} size={14} className="text-muted-foreground" />
            Escalation-Related Audit Events
          </h4>
          <div className="space-y-3">
            {escalationEvents.map((evt, idx) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 bg-amber-50/50 border border-amber-200 rounded-lg"
              >
                <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-amber-500 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{evt.action || evt.notes}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    {evt.actor && (
                      <span className="flex items-center gap-1">
                        <SafeIcon icon={FiIcons.FiUser} size={12} />
                        {evt.actor}
                      </span>
                    )}
                    {evt.date && (
                      <span>{format(new Date(evt.date), 'dd MMM yyyy, HH:mm')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// =============================================================================
// Shared Helper Sub-Components
// =============================================================================

/**
 * Renders a single date row in the progress summary card.
 */
const DateRow = ({ label, date, icon, highlight }) => {
  if (!date) return null;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <SafeIcon icon={icon} size={14} className={clsx('shrink-0', highlight ? 'text-red-500' : 'text-muted-foreground')} />
      <span className="text-xs text-muted-foreground w-24">{label}</span>
      <span className={clsx('text-xs font-medium', highlight ? 'text-red-600 font-bold' : 'text-foreground')}>
        {format(new Date(date), 'dd MMM yyyy')}
      </span>
    </div>
  );
};

/**
 * Mini stat widget for the progress summary card.
 */
const MiniStat = ({ label, value, icon, color }) => (
  <div className="bg-muted/50 rounded-lg p-3 text-center">
    <SafeIcon icon={icon} className={clsx('mx-auto text-lg mb-1', color || 'text-muted-foreground')} />
    <p className="text-xl font-bold text-foreground">{value}</p>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
  </div>
);

/**
 * Summary stat badge for the Assignments tab header row.
 */
const StatBadge = ({ label, value, icon, color }) => (
  <div className={clsx('rounded-lg p-3 flex items-center gap-3', color)}>
    <SafeIcon icon={icon} className="text-lg" />
    <div>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider opacity-70">{label}</p>
    </div>
  </div>
);

/**
 * Renders a single evidence/document item row.
 */
const EvidenceItem = ({ name, type, uploadedBy, uploadedDate, classification, verifiedBy, verifiedDate }) => {
  const FileIcon = getFileIcon(type);
  const isVerified = !!verifiedBy;

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border hover:shadow-sm transition-shadow">
      <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
        <SafeIcon icon={FileIcon} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
          {uploadedBy && (
            <span className="flex items-center gap-1">
              <SafeIcon icon={FiIcons.FiUser} size={11} />
              {uploadedBy}
            </span>
          )}
          {uploadedDate && (
            <span>{format(new Date(uploadedDate), 'dd MMM yyyy')}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {classification && (
          <Badge status={classification} size="sm">
            {classification}
          </Badge>
        )}
        {isVerified ? (
          <Badge status="Completed" size="sm">
            <SafeIcon icon={FiIcons.FiCheckCircle} size={11} className="mr-1" />
            Verified
          </Badge>
        ) : (
          <Badge status="Pending" size="sm">
            Pending Review
          </Badge>
        )}
      </div>
    </div>
  );
};
